import { useState, useEffect } from 'react'
import { Plus, Pencil, Search } from 'lucide-react'
import { useAuth } from '../context/AuthContext'
import { getVoluntarios, crearVoluntario, updateVoluntario, updateVoluntarioRol, toggleVoluntarioActivo, deleteVoluntario, searchVoluntarios } from '../lib/voluntarios'
import FormularioVoluntario from '../components/voluntarios/FormularioVoluntario'
import { usePermiso } from '../hooks/usePermiso'

const Voluntarios = () => {
  const { tienePermiso: puedeCrear } = usePermiso('voluntarios', 'crear')
  const { tienePermiso: puedeEditar } = usePermiso('voluntarios', 'editar')
  const { tienePermiso: puedeEliminar } = usePermiso('voluntarios', 'eliminar')
  const { user } = useAuth()
  const [voluntarios, setVoluntarios] = useState([])
  const [filteredVoluntarios, setFilteredVoluntarios] = useState([])
  const [loading, setLoading] = useState(true)
  const [busqueda, setBusqueda] = useState('')
  const [showForm, setShowForm] = useState(false)
  const [editandoVoluntario, setEditandoVoluntario] = useState(null)
  const [isAdmin] = useState(true) // Ya esta protegido por ruta

  useEffect(() => {
    cargarVoluntarios()
  }, [])

  const cargarVoluntarios = async () => {
    try {
      setLoading(true)
      const data = await getVoluntarios()
      setVoluntarios(data)
      setFilteredVoluntarios(data)
    } catch (err) {
      console.error('Error al cargar voluntarios:', err)
    } finally {
      setLoading(false)
    }
  }

  const handleSearch = async (e) => {
    const termino = e.target.value
    setBusqueda(termino)

    if (termino.length === 0) {
      setFilteredVoluntarios(voluntarios)
      return
    }

    try {
      const results = await searchVoluntarios(termino)
      setFilteredVoluntarios(results)
    } catch (err) {
      console.error('Error en busqueda:', err)
    }
  }

  const handleCreate = async (formData) => {
    await crearVoluntario(formData)
    setShowForm(false)
    await cargarVoluntarios()
  }

  const handleRolChange = async (id, rol) => {
    try {
      await updateVoluntarioRol(id, rol)
      await cargarVoluntarios()
    } catch (err) {
      alert('Error al cambiar rol: ' + err.message)
    }
  }

  const handleToggleActivo = async (id, activo) => {
    try {
      await toggleVoluntarioActivo(id, !activo)
      await cargarVoluntarios()
    } catch (err) {
      alert('Error al cambiar estado: ' + err.message)
    }
  }

  const handleEdit = (voluntario) => {
    setEditandoVoluntario(voluntario)
    setShowForm(true)
  }

  const handleUpdate = async (formData) => {
    if (!editandoVoluntario) return
    const { password, ...updates } = formData
    if (password && password.length >= 6) {
      updates.password = password
    }
    await updateVoluntario(editandoVoluntario.id, updates)
    setShowForm(false)
    setEditandoVoluntario(null)
    await cargarVoluntarios()
  }

  const handleDelete = async (id, nombre) => {
    if (!confirm(`Desactivar voluntario "${nombre}"?`)) return
    try {
      await deleteVoluntario(id)
      await cargarVoluntarios()
    } catch (err) {
      alert('Error al desactivar: ' + err.message)
    }
  }

  return (
    <div className="voluntarios-page">
      <div className="page-header">
        <h1>Voluntarios</h1>
        <p>Gestion de voluntarios y administradores del sistema</p>
      </div>

      <div className="page-toolbar">
        <div className="search-box">
          <Search size={16} style={{ color: '#9CA3AF', flexShrink: 0 }} />
          <input
            type="text"
            placeholder="Buscar por nombre, email o cedula..."
            value={busqueda}
            onChange={handleSearch}
            className="search-input"
          />
        </div>
        {puedeCrear && <button className="btn-add" onClick={() => setShowForm(true)} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <Plus size={16} /> Agregar Voluntario
        </button>}
      </div>

      {loading ? (
        <div className="loading-state">
          <div className="loading-spinner" />
          <p>Cargando voluntarios...</p>
        </div>
      ) : filteredVoluntarios.length === 0 ? (
        <div className="empty-state">
          <p>{busqueda ? 'No se encontraron voluntarios con ese criterio.' : 'No hay voluntarios registrados.'}</p>
        </div>
      ) : (
        <div className="table-container">
          <table className="voluntarios-table">
            <thead>
              <tr>
                <th>Nombre</th>
                <th>Email</th>
                <th>Telefono</th>
                <th>Rol</th>
                <th>Zona</th>
                <th>Estado</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {filteredVoluntarios.map((v) => (
                <tr key={v.id} className={!v.activo ? 'inactive-row' : ''}>
                  <td>
                    <strong>{v.nombre}</strong>
                    <small>{v.cedula}</small>
                  </td>
                  <td>{v.email}</td>
                  <td>{v.telefono || '-'}</td>
                  <td>
                    <select
                      value={v.rol}
                      onChange={(e) => handleRolChange(v.id, e.target.value)}
                      className={`role-select ${v.rol}`}
                    >
                      <option value="voluntario">Voluntario</option>
                      <option value="admin">Admin</option>
                    </select>
                  </td>
                  <td>{v.zona_operativa || '-'}</td>
                  <td>
                    <span className={`status-badge ${v.activo ? 'activo' : 'inactivo'}`}>
                      {v.activo ? 'Activo' : 'Inactivo'}
                    </span>
                  </td>
                  <td>
                    <div className="action-buttons">
                      {puedeEditar && <button
                        className="btn-action info"
                        onClick={() => handleEdit(v)}
                        title="Editar voluntario"
                      >
                        <Pencil size={14} /> Editar
                      </button>}
                      {puedeEliminar && <button
                        className={`btn-action ${v.activo ? 'warning' : 'success'}`}
                        onClick={() => handleToggleActivo(v.id, v.activo)}
                        title={v.activo ? 'Desactivar' : 'Activar'}
                      >
                        {v.activo ? 'Desactivar' : 'Activar'}
                      </button>}
                      {puedeEliminar && <button
                        className="btn-action danger"
                        onClick={() => handleDelete(v.id, v.nombre)}
                        title="Desactivar"
                      >
                        Eliminar
                      </button>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showForm && (
        <FormularioVoluntario
          key={editandoVoluntario ? editandoVoluntario.id : 'nuevo'}
          editando={editandoVoluntario}
          onSubmit={editandoVoluntario ? handleUpdate : handleCreate}
          onCancel={() => { setShowForm(false); setEditandoVoluntario(null) }}
        />
      )}

      <style>{`
        .voluntarios-page {
          padding: 0;
        }
        .page-header {
          margin-bottom: 24px;
        }
        .page-header h1 {
          font-size: 1.5rem;
          font-weight: 700;
          color: #1A365D;
          margin-bottom: 4px;
        }
        .page-header p {
          font-size: 0.88rem;
          color: #718096;
        }
        .page-toolbar {
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 12px;
          margin-bottom: 20px;
          flex-wrap: wrap;
        }
        .search-box {
          flex: 1;
          min-width: 200px;
          max-width: 400px;
        }
        .search-input {
          width: 100%;
          padding: 10px 14px;
          border: 1.5px solid #E2E8F0;
          border-radius: 10px;
          font-size: 0.9rem;
          font-family: 'Inter', sans-serif;
          outline: none;
          transition: border-color 0.2s;
          background: white;
        }
        .search-input:focus {
          border-color: #4299E1;
          box-shadow: 0 0 0 3px rgba(66,153,225,0.12);
        }
        .btn-add {
          padding: 10px 20px;
          background: linear-gradient(135deg, #2B6CB0, #4299E1);
          color: white;
          border: none;
          border-radius: 10px;
          font-size: 0.9rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
          white-space: nowrap;
        }
        .btn-add:hover {
          background: linear-gradient(135deg, #2C5282, #3182CE);
          transform: translateY(-1px);
        }
        .table-container {
          background: white;
          border-radius: 12px;
          border: 1px solid #E2E8F0;
          overflow: hidden;
          overflow-x: auto;
        }
        .voluntarios-table {
          width: 100%;
          border-collapse: collapse;
        }
        .voluntarios-table th {
          background: #F7FAFC;
          padding: 12px 16px;
          font-size: 0.75rem;
          font-weight: 600;
          color: #4A5568;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          text-align: left;
          border-bottom: 1px solid #E2E8F0;
        }
        .voluntarios-table td {
          padding: 12px 16px;
          font-size: 0.88rem;
          color: #2D3748;
          border-bottom: 1px solid #F7FAFC;
        }
        .voluntarios-table tr:hover td {
          background: #F7FAFC;
        }
        .voluntarios-table td strong {
          display: block;
          font-weight: 600;
          color: #1A365D;
        }
        .voluntarios-table td small {
          font-size: 0.75rem;
          color: #A0AEC0;
        }
        .inactive-row td {
          opacity: 0.5;
        }
        .role-select {
          padding: 6px 10px;
          border: 1.5px solid #E2E8F0;
          border-radius: 8px;
          font-size: 0.82rem;
          font-weight: 500;
          font-family: 'Inter', sans-serif;
          cursor: pointer;
          outline: none;
          background: white;
        }
        .role-select:focus {
          border-color: #4299E1;
        }
        .role-select.admin {
          color: #2B6CB0;
          border-color: #BEE3F8;
          background: #EBF8FF;
        }
        .role-select.voluntario {
          color: #2D3748;
          border-color: #E2E8F0;
        }
        .status-badge {
          display: inline-block;
          padding: 4px 12px;
          border-radius: 20px;
          font-size: 0.75rem;
          font-weight: 600;
        }
        .status-badge.activo {
          background: #C6F6D5;
          color: #276749;
        }
        .status-badge.inactivo {
          background: #FED7D7;
          color: #9B2C2C;
        }
        .action-buttons {
          display: flex;
          gap: 6px;
          flex-wrap: wrap;
        }
        .btn-action.info {
          background: #BEE3F8;
          color: #2B6CB0;
        }
        .btn-action.info:hover {
          background: #90CDF4;
        }
        .btn-action {
          padding: 6px 12px;
          border: none;
          border-radius: 8px;
          font-size: 0.78rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
          font-family: 'Inter', sans-serif;
        }
        .btn-action.warning {
          background: #FEFCBF;
          color: #975A16;
        }
        .btn-action.warning:hover {
          background: #F6E05E;
        }
        .btn-action.success {
          background: #C6F6D5;
          color: #276749;
        }
        .btn-action.success:hover {
          background: #9AE6B4;
        }
        .btn-action.danger {
          background: #FED7D7;
          color: #9B2C2C;
        }
        .btn-action.danger:hover {
          background: #FEB2B2;
        }
        .loading-state, .empty-state {
          text-align: center;
          padding: 60px 20px;
          background: white;
          border-radius: 12px;
          border: 1px solid #E2E8F0;
        }
        .loading-state p, .empty-state p {
          color: #718096;
          font-size: 0.9rem;
          margin-top: 12px;
        }
        .loading-spinner {
          width: 32px;
          height: 32px;
          border: 3px solid #E2E8F0;
          border-top-color: #4299E1;
          border-radius: 50%;
          animation: vspin 0.7s linear infinite;
          margin: 0 auto;
        }
        @keyframes vspin {
          to { transform: rotate(360deg); }
        }
        @media (max-width: 768px) {
          .page-toolbar {
            flex-direction: column;
            align-items: stretch;
          }
          .search-box {
            max-width: 100%;
          }
          .voluntarios-table {
            font-size: 0.82rem;
          }
          .voluntarios-table th, .voluntarios-table td {
            padding: 10px 12px;
          }
          .action-buttons {
            flex-direction: column;
          }
        }
      `}</style>
    </div>
  )
}

export default Voluntarios